import { configAtom } from "@/config.ts";
import { type ApiClient, getResponseData } from "@/modules/api/apiClient.ts";
import { getResourceIconClass } from "@/modules/api/resourceTypes.tsx";
import { atomAllLocationsQuery } from "@/modules/location/atomAllLocationsQuery.ts";
import { firewallLink } from "@/modules/network/links.ts";
import { raiseRequestToast, type Toast } from "@/modules/notifications/toast.tsx";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import { constantQueryResult } from "@/utils/query/mergeQueries.ts";
import { optimisticPromise } from "@/utils/query/optimisticPromise.ts";
import { produceQueryData } from "@/utils/query/produceQueryData.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import { mutationOptions } from "@/utils/query/runMutation.ts";
import { type QueryClient, type QueryKey, queryOptions } from "@tanstack/react-query";
import type { Firewall, FirewallCreateBody, FirewallModifyBody } from "@warrenio/api-spec/spec.oats.gen";
import { mapFromEntries, mergeMaps } from "@warrenio/utils/collections/maps";
import { atomFamily } from "jotai-family";
import { atom } from "jotai/vanilla";

//#region basic Types/Contracts
interface Params {
    location: string;
}

export interface FirewallWithType extends Firewall, Params {
    $type: "firewall";
}

type Response = Map<Firewall["uuid"], FirewallWithType>;

const emptyResponse: Response = new Map();

const toastOptions: Partial<Toast> = { icon: getResourceIconClass("firewall") };

//#endregion basic Types/Contracts

//#region helper functions

// NB: Need explicit return type to avoid TypeScript error for `UseQueryOptions` below.
export function getQueryKey(params?: Params): QueryKey {
    return params == null ? ["firewall/list"] : ["firewall/list", params.location];
}

function firewallFromData(data: Firewall, location: string): FirewallWithType {
    return { ...data, $type: "firewall", location };
}

//#endregion helper functions

//#region Queries

function getSingleQuery(apiClient: ApiClient, params: Params) {
    return queryOptions({
        queryKey: getQueryKey(params),
        queryFn: async ({ signal }): Promise<Response> => {
            const { location } = params;
            const response = getResponseData(
                await apiClient.GET("/{location}/network/firewalls", {
                    signal,
                    params: { path: { location } },
                }),
            );
            return mapFromEntries(response, (wall) => [wall.uuid, firewallFromData(wall, location)]);
        },
    });
}

export const firewallQueryAtom = atomFamily((location: string) =>
    atomFromStandardQueryOptions(getSingleQuery, { location }),
);

const allFirewallQueryAtom_real = atomAllLocationsQuery(firewallQueryAtom, (results) => mergeMaps(results));

const emptyQueryResult = constantQueryResult(emptyResponse);

export const allFirewallQueryAtom = atom((get) => {
    if (!get(configAtom).firewallEnabled) {
        return emptyQueryResult;
    }
    return get(allFirewallQueryAtom_real);
});

//#endregion Queries

//#region Mutations

export function createFirewallMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["firewalls/create"],
        async mutationFn({ body, location }: { body: FirewallCreateBody; location: string }) {
            const result = getResponseData(
                await api.POST("/{location}/network/firewalls", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { location } },
                }),
            );
            return firewallFromData(result, location);
        },
        onSuccess(response, { location }) {
            produceQueryData(queryClient, getQueryKey({ location }), emptyResponse, (draft) => {
                draft.set(response.uuid, response);
            });
        },
        onSettled(res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Firewall created",
                error: "Error creating firewall",
                successType: "success",
                viewLink: res ? firewallLink(res) : undefined,
            });
            optimisticPromise(queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) }));
        },
    });
}

export function modifyFirewallMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["firewalls/modify"],
        async mutationFn({ body, location, uuid }: { body: FirewallModifyBody; location: string; uuid: string }) {
            const result = getResponseData(
                await api.PUT("/{location}/network/firewalls/{uuid}", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { location, uuid } },
                }),
            );
            return firewallFromData(result, location);
        },
        onSuccess(response, { location }) {
            produceQueryData(queryClient, getQueryKey({ location }), emptyResponse, (draft) => {
                draft.set(response.uuid, response);
            });
        },
        onSettled(_res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Firewall updated",
                error: "Error updating firewall",
            });
            optimisticPromise(queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) }));
        },
    });
}

export function deleteFirewallMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["firewalls/delete"],
        async mutationFn({ location, uuid }: { location: string; uuid: string }) {
            return getResponseData(
                await api.DELETE("/{location}/network/firewalls/{uuid}", {
                    params: { path: { location, uuid } },
                }),
            );
        },
        async onSettled(_res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Firewall deleted",
                error: "Error deleting firewall",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) });
        },
    });
}

export function addResourceToFirewallMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["firewall/add_resource"],
        async mutationFn({ uuid, location, vm_uuid }: { uuid: string; location: string; vm_uuid: string }) {
            return getResponseData(
                await api.POST("/{location}/network/firewalls/{uuid}/vms", {
                    params: { path: { location, uuid }, query: { vm_uuid } },
                }),
            );
        },
        async onSettled(_res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Resource added to firewall",
                error: "Failed to add resource to firewall",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) });
        },
    });
}

export function removeResourceFromFirewallMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["firewall/remove_resource"],
        async mutationFn({ uuid, location, vm_uuid }: { uuid: string; location: string; vm_uuid: string }) {
            return getResponseData(
                await apiClient.DELETE("/{location}/network/firewalls/{uuid}/vms", {
                    params: { path: { location, uuid }, query: { vm_uuid } },
                }),
            );
        },
        async onSettled(_res, err, { location }) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Resource removed from firewall",
                error: "Failed to remove resource from firewall",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey({ location }) });
        },
    });
}

//#endregion Mutations
